package uz.mint.frontend.i18n

/**
 * Questions and answers, at `/faq` and translated into all three languages.
 *
 * The footer's "Вопросы и ответы" used to open Telegram. That sent a visitor off the site at the
 * exact moment they were deciding whether to buy — and answered nothing until someone replied.
 * Every question here is one that can be answered without a person.
 *
 * **Translated, unlike the offer and the privacy policy.** Those stay Russian because a machine
 * translation of a refund clause can be materially wrong; this is help copy, where a translation
 * is simply the point.
 *
 * The answers are written from what the product actually does. Two in particular are worth
 * keeping honest as the code changes: the one about refunds mirrors the offer's 24–48 hours, and
 * the one about coverage says plainly that we do not run the networks.
 */
fun faqFor(locale: Locale): LegalDocument = when (locale) {
    Locale.RU -> FAQ_RU
    Locale.UZ -> FAQ_UZ
    Locale.EN -> FAQ_EN
}

private val FAQ_RU = LegalDocument(
    revision = "Обновлено 4 августа 2026",
    intro = "Короткие ответы на то, что спрашивают чаще всего. Если вашего вопроса здесь нет — " +
        "напишите нам в Telegram @mintesim, отвечаем в рабочее время.",
    sections = listOf(
        LegalDocument.Section(
            "Что такое eSIM?",
            "Это SIM-карта без пластика: профиль оператора загружается прямо в телефон по " +
                "QR-коду. Обычная симка остаётся на месте — eSIM работает второй линией, поэтому " +
                "ваш номер продолжает принимать звонки и SMS, пока интернет идёт через нас.",
        ),
        LegalDocument.Section(
            "Мой телефон поддерживает eSIM?",
            "Поддерживают iPhone XS и новее, Google Pixel 3 и новее, Samsung Galaxy S20 и новее, " +
                "а также многие модели Huawei, Xiaomi, Motorola и Oppo. Полный список — в разделе " +
                "«Совместимые телефоны» внизу страницы.\n\n" +
                "Быстрая проверка: наберите *#06# — если среди номеров есть строка EID, телефон " +
                "поддерживает eSIM. Отдельно убедитесь, что аппарат не залочен под оператора: " +
                "залоченный телефон не примет чужой профиль, даже если умеет eSIM.",
        ),
        LegalDocument.Section(
            "Как быстро придёт eSIM после оплаты?",
            "Обычно в течение нескольких минут. Профиль появляется в личном кабинете, в разделе " +
                "«Мои eSIM»: там будут QR-код, код активации, объём трафика и срок действия. " +
                "Ничего ждать на почте не нужно — всё на сайте, под вашим номером телефона.",
        ),
        LegalDocument.Section(
            "Как установить?",
            "Отсканируйте QR-код камерой телефона или добавьте профиль вручную кодом активации. " +
                "Пошаговая инструкция для iPhone и Android — в разделе «Как установить eSIM» " +
                "внизу страницы.\n\n" +
                "Устанавливать лучше дома, при рабочем интернете: для загрузки профиля нужна " +
                "сеть. Сам профиль при этом не активируется — отсчёт начнётся уже за границей.",
        ),
        LegalDocument.Section(
            "Когда начинается отсчёт дней?",
            "С первого подключения к сети в стране поездки, а не с момента покупки. Поэтому " +
                "тариф можно купить заранее и установить дома — дни начнут таять только когда " +
                "телефон найдёт сеть на месте.",
        ),
        LegalDocument.Section(
            "Будут ли работать звонки и SMS?",
            "Нет. В тарифы входит только мобильный интернет. Звонить и переписываться можно через " +
                "Telegram, WhatsApp, FaceTime и любые другие приложения — им нужен только интернет.\n\n" +
                "Ваш обычный номер продолжает работать на основной симке, если вы её не отключили.",
        ),
        LegalDocument.Section(
            "Чем платить?",
            "Click и Payme. Оплата в сумах, цена на карточке тарифа — окончательная, скрытых " +
                "комиссий нет.",
        ),
        LegalDocument.Section(
            "Что с возвратом?",
            "Если профиль ещё не активирован, деньги вернём: напишите нам с номером заказа, " +
                "возврат приходит в течение 24–48 часов — срок зависит от того, сколько займёт " +
                "операция у Click или Payme.\n\n" +
                "После активации возврат невозможен: трафик уже выкуплен у оператора. Подробнее — " +
                "в публичной оферте.",
        ),
        LegalDocument.Section(
            "Что если трафик закончится?",
            "Интернет просто перестанет работать — в минус вы не уйдёте и лишнего с вас не " +
                "спишут. Купите ещё один тариф на ту же страну: он встанет как отдельный профиль.",
        ),
        LegalDocument.Section(
            "Интернет не работает, что делать?",
            "Проверьте по порядку: включён ли роуминг данных для профиля Mint, выбран ли он " +
                "источником мобильных данных, не отключён ли режим полёта. Иногда помогает " +
                "перезагрузка или выбор сети вручную в настройках оператора.\n\n" +
                "Если не помогло — напишите в Telegram @mintesim и укажите номер заказа и " +
                "страну: посмотрим статус профиля со своей стороны.",
        ),
        LegalDocument.Section(
            "Можно ли перенести eSIM на другой телефон?",
            "Нет. Профиль привязывается к устройству при установке, и перенести или установить " +
                "его второй раз нельзя. Удалите профиль — и он пропадёт вместе с остатком трафика, " +
                "поэтому удаляйте, только когда тариф отработал.",
        ),
    ),
)

private val FAQ_UZ = LegalDocument(
    revision = "4-avgust 2026-yilda yangilandi",
    intro = "Eng koʻp beriladigan savollarga qisqa javoblar. Savolingiz bu yerda boʻlmasa — " +
        "Telegramda @mintesim ga yozing, ish vaqtida javob beramiz.",
    sections = listOf(
        LegalDocument.Section(
            "eSIM nima?",
            "Bu plastiksiz SIM-karta: operator profili telefonga QR-kod orqali yuklanadi. Odatdagi " +
                "SIM-kartangiz joyida qoladi — eSIM ikkinchi liniya sifatida ishlaydi, shuning " +
                "uchun raqamingizga qoʻngʻiroq va SMS kelaveradi.",
        ),
        LegalDocument.Section(
            "Telefonim eSIM ni qoʻllab-quvvatlaydimi?",
            "iPhone XS va undan yangilari, Google Pixel 3 va undan yangilari, Samsung Galaxy S20 " +
                "va undan yangilari, shuningdek Huawei, Xiaomi, Motorola va Oppo ning koʻp " +
                "modellari. Toʻliq roʻyxat sahifa pastidagi «Mos telefonlar» boʻlimida.\n\n" +
                "Tez tekshirish: *#06# raqamini tering — roʻyxatda EID satri boʻlsa, telefon eSIM " +
                "ni qoʻllab-quvvatlaydi. Telefon operatorga bogʻlanmaganiga ham ishonch hosil qiling.",
        ),
        LegalDocument.Section(
            "Toʻlovdan keyin eSIM qachon keladi?",
            "Odatda bir necha daqiqada. Profil shaxsiy kabinetdagi «Mening eSIM» boʻlimida " +
                "paydo boʻladi: QR-kod, aktivatsiya kodi, trafik hajmi va amal qilish muddati. " +
                "Pochtani kutish shart emas — hammasi saytda, telefon raqamingiz ostida.",
        ),
        LegalDocument.Section(
            "Qanday oʻrnatiladi?",
            "QR-kodni telefon kamerasi bilan skanerlang yoki profilni aktivatsiya kodi bilan " +
                "qoʻlda qoʻshing. iPhone va Android uchun bosqichma-bosqich yoʻriqnoma sahifa " +
                "pastidagi «eSIM ni qanday oʻrnatish» boʻlimida.\n\n" +
                "Uyda, internet bor joyda oʻrnatgan maʼqul: profilni yuklab olish uchun tarmoq " +
                "kerak. Profil bunda faollashmaydi — hisob chet elda boshlanadi.",
        ),
        LegalDocument.Section(
            "Kunlar qachondan sanaladi?",
            "Sotib olingandan emas, borgan mamlakatingizda tarmoqqa birinchi ulanishdan. " +
                "Shuning uchun tarifni oldindan olib, uyda oʻrnatib qoʻysa boʻladi.",
        ),
        LegalDocument.Section(
            "Qoʻngʻiroq va SMS ishlaydimi?",
            "Yoʻq. Tariflarga faqat mobil internet kiradi. Telegram, WhatsApp, FaceTime va " +
                "boshqa ilovalar orqali gaplashish mumkin — ularga faqat internet kerak.\n\n" +
                "Odatdagi raqamingiz asosiy SIM-kartada ishlayveradi.",
        ),
        LegalDocument.Section(
            "Nima bilan toʻlanadi?",
            "Click va Payme. Toʻlov soʻmda, tarif kartochkasidagi narx yakuniy — yashirin " +
                "komissiya yoʻq.",
        ),
        LegalDocument.Section(
            "Pulni qaytarish qanday?",
            "Profil hali faollashtirilmagan boʻlsa, pulni qaytaramiz: buyurtma raqami bilan " +
                "yozing, qaytarish 24–48 soat ichida keladi — muddat Click yoki Payme tomonidagi " +
                "amalga bogʻliq.\n\n" +
                "Faollashtirilgandan keyin qaytarib boʻlmaydi: trafik operatordan sotib olingan. " +
                "Batafsil — ommaviy ofertada.",
        ),
        LegalDocument.Section(
            "Trafik tugasa nima boʻladi?",
            "Internet shunchaki ishlamay qoʻyadi — minusga tushmaysiz va ortiqcha pul yechilmaydi. " +
                "Oʻsha mamlakat uchun yana bir tarif oling: u alohida profil boʻlib oʻrnatiladi.",
        ),
        LegalDocument.Section(
            "Internet ishlamayapti, nima qilay?",
            "Tartib bilan tekshiring: Mint profili uchun maʼlumotlar roumingi yoqilganmi, u mobil " +
                "internet manbasi qilib tanlanganmi, parvoz rejimi oʻchirilganmi. Baʼzan telefonni " +
                "oʻchirib yoqish yoki tarmoqni qoʻlda tanlash yordam beradi.\n\n" +
                "Yordam bermasa — Telegramda @mintesim ga buyurtma raqami va mamlakatni yozing.",
        ),
        LegalDocument.Section(
            "eSIM ni boshqa telefonga koʻchirish mumkinmi?",
            "Yoʻq. Profil oʻrnatishda qurilmaga bogʻlanadi, uni koʻchirib yoki ikkinchi marta " +
                "oʻrnatib boʻlmaydi. Profilni oʻchirsangiz, qolgan trafik bilan birga yoʻqoladi.",
        ),
    ),
)

private val FAQ_EN = LegalDocument(
    revision = "Updated 4 August 2026",
    intro = "Short answers to what people ask most. If your question is not here, message us on " +
        "Telegram @mintesim — we reply during working hours.",
    sections = listOf(
        LegalDocument.Section(
            "What is an eSIM?",
            "A SIM card without the plastic: the operator's profile is loaded straight onto your " +
                "phone from a QR code. Your usual SIM stays where it is — the eSIM runs as a " +
                "second line, so your own number keeps receiving calls and texts.",
        ),
        LegalDocument.Section(
            "Does my phone support eSIM?",
            "iPhone XS and newer, Google Pixel 3 and newer, Samsung Galaxy S20 and newer, plus " +
                "many models from Huawei, Xiaomi, Motorola and Oppo. The full list is under " +
                "\"Compatible phones\" at the bottom of the page.\n\n" +
                "Quick check: dial *#06# — if the list includes an EID, your phone supports eSIM. " +
                "Also make sure it is not carrier-locked: a locked phone will refuse another " +
                "operator's profile even if it supports eSIM.",
        ),
        LegalDocument.Section(
            "How soon does the eSIM arrive?",
            "Usually within a few minutes. The profile appears in your account under \"My eSIMs\": " +
                "QR code, activation code, data allowance and validity. Nothing to wait for by " +
                "email — it is all on the site, under your phone number.",
        ),
        LegalDocument.Section(
            "How do I install it?",
            "Scan the QR code with your phone's camera, or add the profile manually with the " +
                "activation code. Step-by-step instructions for iPhone and Android are under " +
                "\"How to install an eSIM\" at the bottom of the page.\n\n" +
                "Install at home while you have working internet: downloading the profile needs a " +
                "connection. Installing does not activate it — the clock starts abroad.",
        ),
        LegalDocument.Section(
            "When do the days start counting?",
            "From the first connection to a network in your destination, not from purchase. So " +
                "you can buy ahead and install at home without losing days.",
        ),
        LegalDocument.Section(
            "Do calls and SMS work?",
            "No. The plans carry mobile data only. Use Telegram, WhatsApp, FaceTime or anything " +
                "else that runs over the internet.\n\n" +
                "Your usual number keeps working on your main SIM if you leave it switched on.",
        ),
        LegalDocument.Section(
            "How can I pay?",
            "Click and Payme. Payment is in so'm, and the price on the card is final — no hidden " +
                "fees.",
        ),
        LegalDocument.Section(
            "What about refunds?",
            "If the profile has not been activated yet, we refund: message us with your order " +
                "number and the money arrives within 24–48 hours, depending on how long Click or " +
                "Payme takes.\n\n" +
                "After activation a refund is not possible — the data has already been bought " +
                "from the operator. See the public offer for the detail.",
        ),
        LegalDocument.Section(
            "What happens when the data runs out?",
            "The internet simply stops. You cannot go negative and nothing extra is charged. Buy " +
                "another plan for the same country and it installs as a separate profile.",
        ),
        LegalDocument.Section(
            "The internet is not working — what now?",
            "Check in order: data roaming enabled for the Mint profile, the profile selected as " +
                "your mobile data line, aeroplane mode off. A restart or picking the network " +
                "manually sometimes helps.\n\n" +
                "If none of that works, message @mintesim on Telegram with your order number and " +
                "country and we will check the profile from our side.",
        ),
        LegalDocument.Section(
            "Can I move an eSIM to another phone?",
            "No. The profile is bound to the device when installed and cannot be transferred or " +
                "installed twice. Deleting it removes it along with any data left on it.",
        ),
    ),
)
